import React, { useState } from 'react'
import * as ROUTES from 'constants/routes'
import {
  Box,
  Button,
  Flex,
  Link,
  Loading,
  Message,
  Text,
  Table,
  Tooltip,
  useAppTheme,
} from '@workwhile/ui'
import { ColumnDef } from '@tanstack/react-table'
import { formatDistanceStrict } from 'date-fns'
import { formatInTimeZone } from 'date-fns-tz'
import { Info, TriangleAlert } from 'lucide-react'
import { TableEmptyState } from 'components/Table/TableEmptyState'
import { getLocalTimezone } from 'lib/time'
import { useAssessmentsQueueContextValue } from '../context/AssessmentsQueueProvider'

type TableState = ReturnType<
  typeof useAssessmentsQueueContextValue
>['state']['upcoming']

type AssessmentRow = TableState['items'][number]

type PendingEvaluationsTableProps = {
  table: TableState
  onLoadMore: () => void
}

const ONE_HOUR_MS = 60 * 60 * 1000

const RELATIVE_TIME_HEADER_TOOLTIP =
  'Hover a line item to see the full timestamp.'

const SHIFT_STARTS_URGENT_TOOLTIP =
  'The next shift starts very soon, but this worker might have applied for later shifts as well. Click "View" to see more information'

const NameIdCell = ({
  name,
  id,
  to,
}: {
  name: string
  id: number
  to: string
}) => (
  <Text>
    {name || 'Unknown'} (
    <Link to={to} target="_blank">
      {id}
    </Link>
    )
  </Text>
)

const RelativeTimeHeader = ({ label }: { label: string }) => (
  <Flex alignItems="center" gap={1}>
    <Text>{label}</Text>
    <Tooltip content={RELATIVE_TIME_HEADER_TOOLTIP}>
      <Info size={14} />
    </Tooltip>
  </Flex>
)

const RelativeTimeCell = ({
  value,
  now,
  showUrgentWarning = false,
}: {
  value: string
  now: Date
  showUrgentWarning?: boolean
}) => {
  const { colors } = useAppTheme()
  const date = new Date(value)
  const msRemaining = date.getTime() - now.getTime()
  const isUrgent =
    showUrgentWarning && msRemaining > 0 && msRemaining < ONE_HOUR_MS
  const formatted = formatInTimeZone(
    date,
    getLocalTimezone(),
    'MMM dd, yyyy, hh:mm a zzz'
  )

  return (
    <Flex alignItems="center" gap={1}>
      <Text title={formatted}>
        {formatDistanceStrict(date, now, { addSuffix: true })}
      </Text>
      {isUrgent ? (
        <Tooltip content={SHIFT_STARTS_URGENT_TOOLTIP}>
          <Box flexShrink={0}>
            <TriangleAlert size={14} color={colors.warning} />
          </Box>
        </Tooltip>
      ) : null}
    </Flex>
  )
}

const buildColumns = (now: Date): ColumnDef<AssessmentRow>[] => [
  {
    header: 'Worker',
    accessorKey: 'workerName',
    cell: ({ row }) => (
      <NameIdCell
        name={row.original.workerName}
        id={row.original.workerId}
        to={ROUTES.USER_DETAIL.replace(
          ':userId',
          row.original.workerId.toString()
        )}
      />
    ),
  },
  {
    header: 'Company',
    accessorKey: 'companyName',
    cell: ({ row }) => (
      <NameIdCell
        name={row.original.companyName}
        id={row.original.companyId}
        to={ROUTES.COMPANY_DETAIL.replace(
          ':companyId',
          row.original.companyId.toString()
        )}
      />
    ),
  },
  {
    header: 'Position',
    accessorKey: 'positionName',
    cell: ({ row }) => (
      <NameIdCell
        name={row.original.positionName}
        id={row.original.positionId}
        to={ROUTES.POSITION_DETAIL.replace(
          ':positionId',
          row.original.positionId.toString()
        )}
      />
    ),
  },
  {
    header: 'Shift',
    accessorKey: 'shiftId',
    cell: ({ row }) => (
      <Link
        to={ROUTES.SHIFT_DETAIL.replace(
          ':shiftId',
          row.original.shiftId.toString()
        )}
        target="_blank"
      >
        {row.original.shiftId}
      </Link>
    ),
  },
  {
    header: () => <RelativeTimeHeader label="Shift starts" />,
    accessorKey: 'shiftStartsAt',
    cell: ({ row }) => {
      const value = row.original.shiftStartsAt
      if (!value) return '—'
      return <RelativeTimeCell value={value} now={now} showUrgentWarning />
    },
  },
  {
    header: () => <RelativeTimeHeader label="Last call ended" />,
    accessorKey: 'lastCallEndedAt',
    cell: ({ row }) => (
      <RelativeTimeCell value={row.original.lastCallEndedAt} now={now} />
    ),
  },
  {
    header: 'Assessment',
    id: 'assessmentLink',
    enableSorting: false,
    cell: ({ row }) => (
      <Link
        to={ROUTES.WORKER_ASSESSMENT_DETAIL.replace(
          ':positionId',
          row.original.positionId.toString()
        ).replace(':userId', row.original.workerId.toString())}
      >
        View
      </Link>
    ),
  },
]

export const PendingEvaluationsTable = ({
  table,
  onLoadMore,
}: PendingEvaluationsTableProps) => {
  const {
    items,
    isPending,
    isFetching,
    isFetchingNextPage,
    hasNextPage,
    isError,
    errorMessage,
  } = table
  const [now] = useState(() => new Date())
  const columns = buildColumns(now)

  if (isPending) {
    return <Loading />
  }

  if (isError && items.length === 0) {
    return (
      <Message variant="error">
        {errorMessage ||
          'Failed to load pending evaluations. Please try again.'}
      </Message>
    )
  }

  if (items.length === 0) {
    return (
      <TableEmptyState
        isFetching={isFetching}
        emptyMessage="No pending evaluations found"
        emptyStateHeight={50}
      />
    )
  }

  return (
    <Box>
      {isError && errorMessage ? (
        <Box mb={2}>
          <Message variant="error">{errorMessage}</Message>
        </Box>
      ) : null}
      <Table
        columns={columns}
        data={items}
        enableSorting
        textVerticalAlign="top"
      />
      {hasNextPage ? (
        <Flex mt={3}>
          <Button
            variant="secondary"
            loading={isFetchingNextPage || isFetching}
            onClick={onLoadMore}
          >
            Load more
          </Button>
        </Flex>
      ) : null}
    </Box>
  )
}
